import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { fromEvent, merge, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, take, tap } from 'rxjs/operators';

import { AppState } from '../../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../../core/_base/crud';
import {
  Affiliation,
  AffiliationsDataSource,
  AffiliationsPageRequested,
  AffiliationsService,
  AffiliationType,
} from '../../../../../../../core/lists';
import { GeneralAutocompleteComponent } from '../../../../../../partials/layout/general-autocomplete/general-autocomplete.component';

@Component({
  selector: 'kt-affiliations-list',
  templateUrl: './affiliations-list.component.html',
  styleUrls: ['./affiliations-list.component.scss'],
})
export class AffiliationsListComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  entityId: number;
  dataSource: AffiliationsDataSource;
  displayedCols = ['id', 'name', 'email', 'affiliationType', 'phone', 'fax', 'actions'];
  searchBarCols = [
    'searchId',
    'searchName',
    'searchEmail',
    'searchAffiliationType',
    'searchPhone',
    'searchFax',
    'searchActions',
  ];
  showSearchBar = false;
  getAffiliationAutocomplete$;
  getAffiliationTypeAutocomplete$ = this.affiliationsService.searchAffiliationType.bind(
    this.affiliationsService,
  );
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchIdInput') searchIdInput: ElementRef;
  @ViewChild('searchNameInput') searchNameInput: ElementRef;
  @ViewChild('searchEmailInput') searchEmailInput: ElementRef;
  @ViewChild('searchPhoneInput') searchPhoneInput: ElementRef;
  @ViewChild('searchFaxInput') searchFaxInput: ElementRef;
  @ViewChild('affiliationAutocomplete') affiliationAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchAffiliationTypeAutocomplete')
  searchAffiliationTypeAutocomplete: GeneralAutocompleteComponent;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private layoutUtils: LayoutUtilsService,
    private permissionsService: NgxPermissionsService,
    private affiliationsService: AffiliationsService,
  ) {}

  ngOnInit(): void {
    this.dataSource = new AffiliationsDataSource(this.store);

    this.subscriptions.add(
      this.activatedRoute.params.subscribe((p) => {
        this.entityId = p?.id;
        const unrelatedEntitiesIds = p?.id ? [p.id] : [];

        this.getAffiliationAutocomplete$ = this.affiliationsService.findAffiliations.bind(
          this.affiliationsService,
          { unrelatedEntitiesIds },
        );
      }),
    );

    this.subscriptions.add(this.dataSource.entitySubject.subscribe());

    this.subscriptions.add(
      this.sort.sortChange.subscribe((res) => {
        this.paginator.pageIndex = 0;

        if (!res.direction) {
          this.sort.direction = '';
          this.sort.active = '';
        }
      }),
    );

    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadAffiliationsList()))
        .subscribe(),
    );

    this.subscriptions.add(
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(() => this.reloadAffiliationsList()),
        )
        .subscribe(),
    );

    setTimeout(() => this.loadAffiliationsList());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getFilterConfig() {
    const filter: any = {};

    filter.entitiesIds = this.entityId ? [this.entityId] : [];

    if (this.showSearchBar) {
      filter.id = this.searchIdInput?.nativeElement?.value;
      filter.name = this.searchNameInput?.nativeElement?.value;
      filter.email = this.searchEmailInput?.nativeElement?.value;
      filter.affiliationTypeId = this.searchAffiliationTypeAutocomplete?.value?.affiliation_type_id;
      filter.phone = this.searchPhoneInput?.nativeElement?.value;
      filter.fax = this.searchFaxInput?.nativeElement?.value;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
    }

    return filter;
  }

  loadAffiliationsList() {
    const page = new QueryParamsModel(
      this.getFilterConfig(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.store.dispatch(new AffiliationsPageRequested({ page }));
  }

  formatAffiliationOption(affiliation: Affiliation): string {
    return affiliation?.name || '';
  }

  formatAffiliationTypeOption(type: AffiliationType): string {
    return type?.affiliation_type_name || '';
  }

  reloadAffiliationsList(): void {
    this.paginator.pageIndex = 0;

    this.loadAffiliationsList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchIdInput.nativeElement.value = '';
    this.searchNameInput.nativeElement.value = '';
    this.searchEmailInput.nativeElement.value = '';
    this.searchAffiliationTypeAutocomplete.setValue(null);
    this.searchAffiliationTypeAutocomplete.updateValueAndValidity();
    this.searchPhoneInput.nativeElement.value = '';
    this.searchFaxInput.nativeElement.value = '';
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadAffiliationsList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchIdInput.nativeElement.focus());
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadAffiliationsList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadAffiliationsList();
  }

  canViewAffiliation(): boolean {
    return !!this.permissionsService.getPermission('canViewAffiliation');
  }

  addAffiliation(event: Event): void {
    event.stopPropagation();

    localStorage.setItem('affiliationsListPath', this.router.url);
    this.router.navigateByUrl('lists/affiliations/add');
  }

  updateViewAffiliation(event: Event, mode: 'UPDATE' | 'VIEW', affiliation: Affiliation): void {
    event.stopPropagation();

    localStorage.setItem('affiliationsListPath', this.router.url);
    this.router.navigateByUrl(
      `lists/affiliations/${mode === 'UPDATE' ? 'edit' : 'view'}/${affiliation.id}`,
    );
  }

  deleteAffiliation(event: Event, affiliation: Affiliation): void {
    event.stopPropagation();

    const title = this.translate.instant('AFFILIATION.DELETE_AFFILIATION.TITLE');
    const description = this.translate.instant('AFFILIATION.DELETE_AFFILIATION.DESCRIPTION');
    const waitDescription = this.translate.instant(
      'AFFILIATION.DELETE_AFFILIATION.WAIT_DESCRIPTION',
    );
    const dialogRef = this.layoutUtils.generalDialog(title, description, waitDescription);

    dialogRef
      .afterClosed()
      .pipe(
        switchMap((confirmed) =>
          confirmed ? this.affiliationsService.deleteAffiliation(affiliation.id) : of(null),
        ),
        take(1),
      )
      .subscribe((res) => {
        if (!res?.ok) return;

        const message = this.translate.instant('AFFILIATION.DELETE_AFFILIATION.MESSAGE');

        this.reloadAffiliationsList();
        this.layoutUtils.showActionNotification(message, MessageType.Delete, 3000);
      });
  }

  handleAffiliationChange(affiliation: Affiliation): void {
    if (!affiliation?.id) return;

    this.linkUnlinkAffiliation(null, affiliation, true);

    this.affiliationAutocomplete?.setValue(null);
    this.affiliationAutocomplete?.updateValueAndValidity();
  }

  linkUnlinkAffiliation(event: Event, affiliation: Affiliation, linked: boolean): void {
    event?.stopPropagation();

    this.affiliationsService
      .linkUnlinkAffiliation(affiliation.id, this.entityId, linked)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res?.ok) return;

        const message = this.translate.instant(
          `AFFILIATION.${linked ? 'LINK' : 'UNLINK'}_AFFILIATION_MESSAGE`,
        );

        this.reloadAffiliationsList();
        this.layoutUtils.showActionNotification(message, MessageType.Update, 3000);
      });
  }
}
